import { ChevronRightIcon } from "@primer/octicons-react";
import { Label, Text } from "@primer/react";
import { Alert } from "../components/Alert/Alert.jsx";
import { AssetIdentity, assetHref } from "../components/AssetIdentity/AssetIdentity.jsx";
import { ComplianceResultItem } from "../components/ComplianceResultItem/ComplianceResultItem.jsx";
import { formatPercentage } from "../components/EditablePercentage/EditablePercentage.jsx";
import { ExpansionSection } from "../components/ExpansionSection/ExpansionSection.jsx";
import { PageHeading } from "../components/PageHeading/PageHeading.jsx";
import { PolicyFinding } from "../components/PolicyFinding/PolicyFinding.jsx";
import { PolicyIdentity, policyHref } from "../components/PolicyIdentity/PolicyIdentity.jsx";
import { amountForBasisPoints, formatPortfolioAmount, usePortfolioValue } from "../components/PortfolioValue/PortfolioValue.jsx";
import { propertyHref } from "../components/PropertyIdentity/PropertyIdentity.jsx";
import { propertyValueHref } from "../components/PropertyValueIdentity/PropertyValueIdentity.jsx";
import { RecordList } from "../components/RecordList/RecordList.jsx";
import { Section, SectionStack } from "../components/Section/Section.jsx";
import { evaluateCompliance } from "../domain/compliance.js";
import { effectiveValue } from "../domain/document.js";
import { useApplicationDocument } from "../storage/useApplicationDocument.js";
import { usePresentationState } from "../storage/presentationState.js";
import "./PropertyValuePage.css";

export function PropertyValuePage({ profileId: profileIdOverride, propertyId, repository, valueId }) {
  const application = useApplicationDocument(repository);
  if (application.error && !application.document) return <Alert tone="danger" heading="Unable to open Regulator">{application.error.message}</Alert>;
  if (!application.document) return <PropertyValueLoading />;
  return <PropertyValueContent document={application.document} profileId={application.profile?.id ?? profileIdOverride} propertyId={propertyId} valueId={valueId} />;
}

function PropertyValueContent({ document, profileId, propertyId, valueId }) {
  const property = document.properties.find((item) => item.id === propertyId);
  const value = property?.values.find((item) => item.id === valueId);
  if (!property || !value) return <MissingPropertyValue property={property} propertyId={propertyId} valueId={valueId} />;

  const assets = document.assets.filter((asset) => effectiveValue(document, asset.id, property.id).id === value.id);
  const allocation = assets.reduce((total, asset) => total + assetAllocation(document, asset.id), 0);
  const valueIndex = property.values.findIndex((item) => item.id === value.id);

  return <article className="property-value-page">
    <PageHeading
      description={<PropertyValueSummary allocation={allocation} assetCount={assets.length} isDefault={valueIndex === 0} />}
      titleLabel={`${value.name} ${property.name}`}
    >{value.name}<Text as="span" className="property-value-title-context"> {property.name}</Text></PageHeading>
    <SectionStack>
      <Section heading="Definition" description="How this value behaves within its property.">
        <PropertyValueDefinition document={document} isDefault={valueIndex === 0} property={property} value={value} />
      </Section>
      <Section heading="Assets" description={`Assets whose effective ${property.name} value is ${value.name}.`}>
        <PropertyValueAssets assets={assets} document={document} profileId={profileId} />
      </Section>
      <Section heading="Current compliance" description="Direct and property-wide policy results for this value.">
        <PropertyValueCompliance document={document} property={property} value={value} />
      </Section>
    </SectionStack>
  </article>;
}

function PropertyValueSummary({ allocation, assetCount, isDefault }) {
  const { value } = usePortfolioValue();
  return <span className="property-value-summary">
    {isDefault ? <Label variant="accent">Default</Label> : null}
    <Text className="property-value-summary-item">{assetCount} {assetCount === 1 ? "asset" : "assets"}</Text>
    <Text className="property-value-summary-item">· {formatPercentage(allocation)}% allocation</Text>
    {value !== null ? <Text className="property-value-summary-item">· {formatPortfolioAmount(amountForBasisPoints(value, allocation))}</Text> : null}
  </span>;
}

function PropertyValueDefinition({ document, isDefault, property, value }) {
  const availability = valueAvailability(document, value, isDefault);
  return <dl className="property-value-definition">
    <div><dt>Property</dt><dd><a href={propertyHref(property.id)}>{property.name}</a></dd></div>
    <div><dt>Availability</dt><dd>{availability.href ? <a href={availability.href}>{availability.label}</a> : availability.label}</dd></div>
  </dl>;
}

function valueAvailability(document, value, isDefault) {
  if (isDefault || !value.visibility) return { label: "Always available" };
  const property = document.properties.find((item) => item.id === value.visibility.propertyId);
  const condition = property?.values.find((item) => item.id === value.visibility.valueId);
  if (!property || !condition) return { label: "Availability condition is missing" };
  return { href: propertyValueHref(property.id, condition.id), label: `When ${property.name} is ${condition.name}` };
}

function PropertyValueAssets({ assets, document, profileId }) {
  const [expansion, setExpansion] = usePresentationState({
    defaultValue: { allocated: true, unallocated: false }, lifetime: "session", name: "asset-groups", page: "property-value", profileId, scope: "profile", validate: assetGroupExpansion
  });
  if (!assets.length) return <Text className="property-value-empty">No assets currently have this effective value.</Text>;
  const sortedAssets = [...assets].sort((left, right) => assetAllocation(document, right.id) - assetAllocation(document, left.id)
    || left.name.localeCompare(right.name, undefined, { numeric: true, sensitivity: "base" })
    || left.id.localeCompare(right.id, undefined, { numeric: true, sensitivity: "base" }));
  const allocated = sortedAssets.filter((asset) => assetAllocation(document, asset.id) > 0);
  const unallocated = sortedAssets.filter((asset) => assetAllocation(document, asset.id) === 0);
  return <div className="property-value-asset-groups">
    {allocated.length ? <PropertyValueAssetGroup assets={allocated} document={document} expanded={expansion.allocated} label="Allocated" onExpandedChange={(expanded) => setExpansion((current) => ({ ...current, allocated: expanded }))} /> : null}
    {unallocated.length ? <PropertyValueAssetGroup assets={unallocated} document={document} expanded={expansion.unallocated} label="Unallocated" onExpandedChange={(expanded) => setExpansion((current) => ({ ...current, unallocated: expanded }))} /> : null}
  </div>;
}

function PropertyValueAssetGroup({ assets, document, expanded, label, onExpandedChange }) {
  return <ExpansionSection className="property-value-asset-group" expanded={expanded}>
    <button className="property-value-asset-summary" type="button" aria-expanded={expanded} onClick={() => onExpandedChange(!expanded)}>
      <ChevronRightIcon aria-hidden="true" />
      <strong>{label}</strong>
      <Text>{assets.length}</Text>
    </button>
    {expanded ? <RecordList className="property-value-assets">{assets.map((asset) => <div className="property-value-asset record-row" key={asset.id}>
    <AssetIdentity asset={asset} href={assetHref(asset.id)} />
    <Text className="property-value-asset-allocation">{formatPercentage(assetAllocation(document, asset.id))}%</Text>
  </div>)}</RecordList> : null}
  </ExpansionSection>;
}

function assetGroupExpansion(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  if (typeof value.allocated !== "boolean" || typeof value.unallocated !== "boolean") return null;
  return { allocated: value.allocated, unallocated: value.unallocated };
}

function PropertyValueCompliance({ document, property, value }) {
  const findings = applicablePolicyResults(document, property.id, value.id);
  if (!findings.length) return <Text className="property-value-empty">No current policy results apply to this value.</Text>;
  return <RecordList className="property-value-policies">{findings.map(({ policy, relationship, result }) => {
    const summary = <><PolicyIdentity document={document} policy={policy} variant="subject" /><Text className="property-value-policy-allocation">({formatPercentage(result.basisPoints)}%)</Text></>;
    const detail = <><Text className="property-value-policy-relationship">{relationship}</Text><PolicyFinding document={document} policy={policy} result={result} showIdentity={false} /></>;
    return <ComplianceResultItem detail={detail} href={policyHref(policy.id)} key={policy.id} status={result.status} summary={summary} />;
  })}</RecordList>;
}

function applicablePolicyResults(document, propertyId, valueId) {
  return evaluateCompliance(document).flatMap(({ policy, results }) => {
    const direct = policy.scope.type === "specific"
      && policy.scope.propertyId === propertyId
      && policy.scope.valueId === valueId;
    const propertyWide = policy.scope.type === "concentration"
      && policy.scope.dimension.type === "property"
      && policy.scope.dimension.propertyId === propertyId;
    if (!direct && !propertyWide) return [];
    const result = results.find((item) => item.subject.type === "property-value" && item.subject.propertyId === propertyId && item.subject.valueId === valueId);
    return result ? [{ policy, relationship: direct ? "Direct policy" : "Property-wide rule", result }] : [];
  }).sort((left, right) => Number(left.relationship !== "Direct policy") - Number(right.relationship !== "Direct policy"));
}

function assetAllocation(document, assetId) {
  return document.allocations.find((item) => item.assetId === assetId)?.basisPoints ?? 0;
}

function MissingPropertyValue({ property, propertyId, valueId }) {
  const destination = property ? propertyHref(property.id) : "#/properties";
  const destinationLabel = property ? `Return to ${property.name}` : "Return to Properties";
  return <article className="property-value-page"><PageHeading description="This property value may have been renamed or deleted.">Property value not found</PageHeading><Alert tone="warning" heading={`Property value “${valueId}” was not found`}>The requested value does not exist in {property?.name ?? `property “${propertyId}”`}. <a href={destination}>{destinationLabel}</a></Alert></article>;
}

function PropertyValueLoading() {
  return <article className="property-value-page"><PageHeading description="Loading property value…">Property value</PageHeading></article>;
}
